/**
 * Phone board zoom (UX-SPEC §3): centring a square in the zoomed scroller, and
 * following the newest placed tile — without sliding the board under the
 * player's finger when that tile is already comfortably in view.
 */
import { useEffect, type RefObject } from 'react';
import type { Coord } from '@wordloom/engine';

type Box = Pick<DOMRectReadOnly, 'left' | 'top' | 'right' | 'bottom' | 'width' | 'height'>;

function cellIn(scroller: HTMLElement, at: Coord): HTMLElement | null {
  return scroller.querySelector<HTMLElement>(`[data-cell="${at.row},${at.col}"]`);
}

/** Scroll `scroller` so the square (row, col) sits in its centre. */
export function centreOn(scroller: HTMLElement, at: Coord, smooth: boolean): void {
  const cell = cellIn(scroller, at);
  if (!cell) return;
  const s = scroller.getBoundingClientRect();
  const c = cell.getBoundingClientRect();
  scroller.scrollTo({
    left: scroller.scrollLeft + (c.left + c.width / 2) - (s.left + s.width / 2),
    top: scroller.scrollTop + (c.top + c.height / 2) - (s.top + s.height / 2),
    behavior: smooth ? 'smooth' : 'auto',
  });
}

/**
 * True when `cell` lies inside `view` with at least one cell of margin on
 * every side: the square and all its neighbours are visible, so the next tap
 * (usually the next square along the word) lands where the player aims.
 */
export function isComfortablyInView(view: Box, cell: Box): boolean {
  return (
    cell.left - cell.width >= view.left &&
    cell.right + cell.width <= view.right &&
    cell.top - cell.height >= view.top &&
    cell.bottom + cell.height <= view.bottom
  );
}

/**
 * While zoomed, keep the newest placed tile (`at`) in view. It re-centres only
 * when the tile is at or past the scroller's edge; re-centring on every
 * placement slid the board and the next tap landed a column or two off.
 */
export function useFollowPlacement(
  scrollRef: RefObject<HTMLElement | null>,
  zoomed: boolean,
  at: Coord | undefined,
  smooth: boolean,
): void {
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!zoomed || !at || !scroller) return;
    const cell = cellIn(scroller, at);
    if (!cell) return;
    if (isComfortablyInView(scroller.getBoundingClientRect(), cell.getBoundingClientRect())) return;
    centreOn(scroller, at, smooth);
  }, [scrollRef, zoomed, at, smooth]);
}
